/**
 * AdminReconciliation — accounting reconciliation workspace.
 *
 * Corrects investor/accounting numbers by letting admins record VERIFIED
 * client payments & talent payouts across Stripe + external rails (BlueVine,
 * Melio, ACH, check, etc.) without overwriting history. Includes the verified
 * summary report, invoice/payout reconciliation queues, manual reconcile,
 * CSV dry-run + commit, and an audit trail.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  ArrowLeft, RefreshCw, Loader2, Upload, CheckCircle2, AlertTriangle,
  FileText, History, DollarSign, PackageCheck,
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}).format(n || 0);

const BADGE_STYLES = {
  'Paid In App': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Paid Externally': 'bg-sky-100 text-sky-800 border-sky-300',
  'Partially Paid': 'bg-amber-100 text-amber-800 border-amber-300',
  'Unpaid': 'bg-slate-100 text-slate-700 border-slate-300',
  'Needs Reconciliation': 'bg-rose-100 text-rose-800 border-rose-300',
};

export default function AdminReconciliation() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState({ payment_sources: [], payout_sources: [], non_revenue_types: [] });
  const [invoices, setInvoices] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [audit, setAudit] = useState([]);

  // modal state
  const [modal, setModal] = useState(null); // {type:'invoice'|'payout', row}
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // CSV state
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o, inv, po, au] = await Promise.all([
        api.get('/admin/reconciliation/summary'),
        api.get('/admin/reconciliation/options'),
        api.get('/admin/reconciliation/queue/invoices'),
        api.get('/admin/reconciliation/queue/payouts'),
        api.get('/admin/reconciliation/audit?limit=100'),
      ]);
      setSummary(s.data); setOptions(o.data);
      setInvoices(inv.data.invoices || []); setPayouts(po.data.payouts || []);
      setAudit(au.data.audit || []);
    } catch (e) {
      toast.error('Failed to load reconciliation data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openInvoice = (row) => {
    setForm({ amount: row.invoiced, payment_source: 'bluevine', is_client_revenue: true,
      payment_date: '', transaction_reference: '', platform_fee: '', talent_cost: '', adjustments: '', notes: '' });
    setModal({ type: 'invoice', row });
  };
  const openPayout = (row) => {
    setForm({ amount: row.amount, payout_source: 'melio', settled_date: '', transaction_reference: '', notes: '' });
    setModal({ type: 'payout', row });
  };

  const submitModal = async () => {
    setSaving(true);
    try {
      if (modal.type === 'invoice') {
        await api.post(`/admin/reconciliation/invoice/${modal.row.invoice_id}`, form);
      } else {
        await api.post(`/admin/reconciliation/payout/${modal.row.payout_id}`, form);
      }
      toast.success('Reconciled & logged to audit trail');
      setModal(null); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconcile failed');
    } finally { setSaving(false); }
  };

  const runPreview = async () => {
    if (!csvText.trim()) { toast.error('Paste CSV first'); return; }
    try {
      const r = await api.post('/admin/reconciliation/csv/preview', { csv: csvText });
      setPreview(r.data);
      toast.success(`Dry-run: ${r.data.summary.total_rows} rows, ${r.data.summary.conflicts} conflicts`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Preview failed'); }
  };

  const commitCsv = async () => {
    const rows = (preview?.rows || [])
      .filter((r) => r.status === 'ok' && r.invoice_id)
      .map((r) => ({
        invoice_id: r.invoice_id,
        amount: parseFloat(r.raw.amount_paid || 0),
        payment_source: (r.raw.payment_source || '').toLowerCase().replace(/[ /-]/g, '_'),
        payment_date: r.raw.payment_date || null,
        transaction_reference: r.raw.transaction_reference || r.raw.reference || null,
        is_client_revenue: true,
        action: 'apply',
      }));
    if (!rows.length) { toast.error('No clean rows to commit'); return; }
    setCommitting(true);
    try {
      const r = await api.post('/admin/reconciliation/csv/commit', { rows });
      toast.success(`Committed ${r.data.applied}, skipped ${r.data.skipped}`);
      setPreview(null); setCsvText(''); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Commit failed'); }
    finally { setCommitting(false); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-cyan-600" /></div>;
  }

  const d = summary?.delivery || {};
  const f = summary?.financial || {};
  const q = summary?.queues || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-reconciliation-page">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="recon-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Accounting Reconciliation</h1>
              <p className="text-sm text-slate-500">Verify client payments & talent payouts across Stripe + external rails. Investor financials stay paused until reconciled.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="recon-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Delivery */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard testid="stat-verified-delivered" label="Verified Delivered Bookings" value={d.verified_delivered_bookings} icon={<PackageCheck className="w-4 h-4" />} tone="emerald" />
          <StatCard testid="stat-likely-delivered" label="Likely Delivered (review)" value={d.likely_delivered_pending_review} tone="amber" />
          <StatCard testid="stat-needs-review" label="Needs Review" value={d.needs_review_bookings} tone="rose" />
          <StatCard testid="stat-verified-events" label="Verified Delivered Events" value={d.verified_delivered_events} tone="cyan" />
        </div>
        {/* Financial */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard testid="stat-invoiced-gbv" label="Invoiced GBV (excl. cancelled)" value={fmt(f.invoiced_gbv_excl_cancellations)} tone="slate" />
          <StatCard testid="stat-verified-cash" label="Verified Client Cash Collected" value={fmt(f.total_verified_client_cash_collected)} icon={<DollarSign className="w-4 h-4" />} tone="emerald" />
          <StatCard testid="stat-outstanding-ar" label="Outstanding A/R" value={fmt(f.outstanding_accounts_receivable)} tone="amber" />
          <StatCard testid="stat-verified-payouts" label="Verified Talent Payouts" value={fmt(f.total_verified_talent_payouts)} tone="cyan" />
        </div>

        <Tabs defaultValue="invoices">
          <TabsList data-testid="recon-tabs">
            <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices ({q.unreconciled_invoices ?? invoices.length})</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts ({q.unreconciled_payouts ?? payouts.length})</TabsTrigger>
            <TabsTrigger value="csv" data-testid="tab-csv">CSV Import</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit Trail</TabsTrigger>
          </TabsList>

          {/* INVOICES */}
          <TabsContent value="invoices">
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoice-queue-table">
                <thead><tr className="text-left text-xs uppercase text-slate-500 border-b">
                  <th className="p-3">Invoice</th><th className="p-3">Badge</th>
                  <th className="p-3 text-right">Invoiced</th><th className="p-3 text-right">Collected</th>
                  <th className="p-3">Status</th><th className="p-3"></th></tr></thead>
                <tbody>
                  {invoices.map((r) => (
                    <tr key={r.invoice_id} className="border-b hover:bg-slate-50" data-testid={`invoice-row-${r.invoice_id}`}>
                      <td className="p-3 font-medium">{r.invoice_number || r.invoice_id.slice(0, 8)}</td>
                      <td className="p-3"><Badge variant="outline" className={BADGE_STYLES[r.badge] || ''} data-testid={`invoice-badge-${r.invoice_id}`}>{r.badge}</Badge></td>
                      <td className="p-3 text-right">{fmt(r.invoiced)}</td>
                      <td className="p-3 text-right">{fmt(r.collected)}</td>
                      <td className="p-3 text-slate-500">{r.reconciled ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />reconciled</span> : r.payment_status}</td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => openInvoice(r)} data-testid={`reconcile-invoice-${r.invoice_id}`}>Reconcile</Button>
                      </td>
                    </tr>
                  ))}
                  {!invoices.length && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No unreconciled invoices</td></tr>}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* PAYOUTS */}
          <TabsContent value="payouts">
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="payout-queue-table">
                <thead><tr className="text-left text-xs uppercase text-slate-500 border-b">
                  <th className="p-3">Payout</th><th className="p-3 text-right">Amount</th>
                  <th className="p-3">Status</th><th className="p-3">Reason</th><th className="p-3"></th></tr></thead>
                <tbody>
                  {payouts.map((r) => (
                    <tr key={r.payout_id} className="border-b hover:bg-slate-50" data-testid={`payout-row-${r.payout_id}`}>
                      <td className="p-3 font-medium">{r.payout_id.slice(0, 8)}</td>
                      <td className="p-3 text-right">{fmt(r.amount)}</td>
                      <td className="p-3">{r.reconciled ? <span className="text-emerald-600">reconciled</span> : <Badge variant="outline" className="bg-rose-100 text-rose-800 border-rose-300">{r.status}</Badge>}</td>
                      <td className="p-3 text-slate-500 text-xs">{r.failed_reason || '—'}</td>
                      <td className="p-3 text-right"><Button size="sm" variant="outline" onClick={() => openPayout(r)} data-testid={`reconcile-payout-${r.payout_id}`}>Reconcile</Button></td>
                    </tr>
                  ))}
                  {!payouts.length && <tr><td colSpan={5} className="p-6 text-center text-slate-400">No payouts needing reconciliation</td></tr>}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>

          {/* CSV */}
          <TabsContent value="csv">
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Bulk reconciliation (dry-run first — nothing saves until you commit)</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-slate-500">Columns: invoice, booking, client, event, invoice_amount, amount_paid, payment_source, payment_date, talent_payout, talent_payout_source, completion_status</p>
                <input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="csv-file-input" className="text-sm" />
                <Textarea rows={6} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder="invoice,amount_paid,payment_source,payment_date&#10;INV-...,912,bluevine,2026-06-30" data-testid="csv-textarea" />
                <div className="flex gap-2">
                  <Button onClick={runPreview} data-testid="csv-preview-btn"><Upload className="w-4 h-4 mr-1" /> Dry-run Preview</Button>
                  {preview && <Button variant="default" className="bg-emerald-600 hover:bg-emerald-700" onClick={commitCsv} disabled={committing} data-testid="csv-commit-btn">{committing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Commit clean rows'}</Button>}
                </div>
                {preview && (
                  <div className="mt-3" data-testid="csv-preview-result">
                    <div className="flex gap-3 text-xs mb-2">
                      <span>Total {preview.summary.total_rows}</span>
                      <span className="text-emerald-600">OK {preview.summary.ok}</span>
                      <span className="text-rose-600">Conflicts {preview.summary.conflicts}</span>
                      <span className="text-slate-500">Unmatched {preview.summary.unmatched}</span>
                      <span className="text-amber-600">Review {preview.summary.needs_review}</span>
                    </div>
                    <div className="border rounded max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="text-left text-slate-500 border-b"><th className="p-2">Row</th><th className="p-2">Status</th><th className="p-2">Proposed / Conflicts</th></tr></thead>
                        <tbody>
                          {preview.rows.map((r) => (
                            <tr key={r.row} className="border-b">
                              <td className="p-2">{r.row}</td>
                              <td className="p-2">{r.status === 'ok' ? <span className="text-emerald-600">ok</span> : r.status === 'conflict' ? <span className="text-rose-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />conflict</span> : <span className="text-amber-600">{r.status}</span>}</td>
                              <td className="p-2">{[...(r.actions || []), ...(r.conflicts || [])].join(' · ') || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AUDIT */}
          <TabsContent value="audit">
            <Card><CardHeader><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Change audit trail</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="audit-table">
                  <thead><tr className="text-left text-xs uppercase text-slate-500 border-b">
                    <th className="p-3">When</th><th className="p-3">Entity</th><th className="p-3">Action</th>
                    <th className="p-3">Field</th><th className="p-3">Old → New</th><th className="p-3">Admin</th></tr></thead>
                  <tbody>
                    {audit.map((a, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-3 text-xs text-slate-500">{a.at ? new Date(a.at).toLocaleString() : '—'}</td>
                        <td className="p-3">{a.entity_type}</td><td className="p-3">{a.action}</td>
                        <td className="p-3">{a.field || '—'}</td>
                        <td className="p-3 text-xs">{(a.old_value ?? '—')} → <b>{a.new_value ?? '—'}</b></td>
                        <td className="p-3 text-xs text-slate-500">{a.admin || '—'}</td>
                      </tr>
                    ))}
                    {!audit.length && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No changes logged yet</td></tr>}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reconcile modal */}
      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="reconcile-modal">
          <DialogHeader><DialogTitle>Reconcile {modal?.type === 'invoice' ? 'invoice' : 'payout'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount</Label>
              <Input type="number" value={form.amount ?? ''} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="form-amount" />
            </div>
            <div>
              <Label>{modal?.type === 'invoice' ? 'Payment source' : 'Payout source'}</Label>
              <Select value={modal?.type === 'invoice' ? form.payment_source : form.payout_source}
                onValueChange={(v) => setForm({ ...form, [modal.type === 'invoice' ? 'payment_source' : 'payout_source']: v })}>
                <SelectTrigger data-testid="form-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(modal?.type === 'invoice' ? options.payment_sources : options.payout_sources).map((s) => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{modal?.type === 'invoice' ? 'Payment date' : 'Settled date'}</Label>
              <Input type="date" value={(modal?.type === 'invoice' ? form.payment_date : form.settled_date) || ''}
                onChange={(e) => setForm({ ...form, [modal.type === 'invoice' ? 'payment_date' : 'settled_date']: e.target.value })} data-testid="form-date" />
            </div>
            <div>
              <Label>Transaction reference</Label>
              <Input value={form.transaction_reference || ''} onChange={(e) => setForm({ ...form, transaction_reference: e.target.value })} placeholder="e.g. BlueVine deposit #" data-testid="form-ref" />
            </div>
            {modal?.type === 'invoice' && (
              <>
                <div className="flex items-center gap-2">
                  <Checkbox id="iscr" checked={form.is_client_revenue} onCheckedChange={(v) => setForm({ ...form, is_client_revenue: !!v })} data-testid="form-is-client-revenue" />
                  <Label htmlFor="iscr" className="cursor-pointer">This is a client invoice payment (counts as revenue)</Label>
                </div>
                {!form.is_client_revenue && (
                  <div>
                    <Label>Non-revenue type (financing/advance/transfer — NOT revenue)</Label>
                    <Select value={form.non_revenue_type || 'financing'} onValueChange={(v) => setForm({ ...form, non_revenue_type: v })}>
                      <SelectTrigger data-testid="form-nonrev-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{options.non_revenue_types.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Platform fee</Label><Input type="number" value={form.platform_fee || ''} onChange={(e) => setForm({ ...form, platform_fee: e.target.value })} data-testid="form-fee" /></div>
                  <div><Label>Talent cost</Label><Input type="number" value={form.talent_cost || ''} onChange={(e) => setForm({ ...form, talent_cost: e.target.value })} data-testid="form-talent-cost" /></div>
                </div>
              </>
            )}
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={submitModal} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="form-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save reconciliation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TONE = {
  emerald: 'text-emerald-700', amber: 'text-amber-700', rose: 'text-rose-700',
  cyan: 'text-cyan-700', slate: 'text-slate-800',
};
function StatCard({ label, value, icon, tone = 'slate', testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">{icon}{label}</div>
        <div className={`text-2xl font-bold mt-1 ${TONE[tone]}`}>{value ?? '—'}</div>
      </CardContent>
    </Card>
  );
}
